import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Inbox, Phone, MessageSquare, Moon, CheckCircle2, XCircle, RotateCw, Trash2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { SmsDialog } from "./SmsDialog";
import {
  RECURRING_STATUSES, recurringStatusColors, buildRecurringSms, firstName, fmtDateOnly, fmtDate,
} from "./leadUtils";
import { useConfig } from "../../hooks/useConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-10 rounded-sm placeholder:text-zinc-600";

export const RecurringTab = ({ onAuthError }) => {
  const config = useConfig();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [smsItem, setSmsItem] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/recurring`);
      setItems(res.data);
    } catch (err) {
      if (!onAuthError(err)) toast.error("Failed to load reminders");
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const patch = async (id, updates, msg) => {
    try {
      await axios.patch(`${API}/admin/recurring/${id}`, updates);
      toast.success(msg);
      fetchAll();
    } catch (err) {
      if (!onAuthError(err)) toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const snooze = (item) => {
    const d = window.prompt("Snooze — remind again on (YYYY-MM-DD):", item.next_reminder_date || "");
    if (!d) return;
    patch(item.recurring_id, { status: "Snoozed", next_reminder_date: d }, `Snoozed until ${d}`);
  };

  const completeAndNext = async (item) => {
    const d = window.prompt("Service completed! Next service due (YYYY-MM-DD):");
    if (!d) return;
    try {
      await axios.post(`${API}/admin/recurring/${item.recurring_id}/next`, { next_service_date: d });
      toast.success("Marked complete — next reminder created");
      fetchAll();
    } catch (err) {
      if (!onAuthError(err)) toast.error("Could not create next reminder");
    }
  };

  const logSend = (item) => async (message) => {
    try {
      await axios.post(`${API}/admin/recurring/${item.recurring_id}/log-send`, { message, channel: "sms" });
      toast.success("Reminder logged as sent");
      fetchAll();
    } catch {
      toast.error("Could not log message");
    }
  };

  const removeRecurring = async (r) => {
    if (!window.confirm(`Delete recurring reminder for ${r.customer_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/recurring/${r.recurring_id}`);
      toast.success("Reminder deleted");
      fetchAll();
    } catch (err) {
      if (!onAuthError(err)) toast.error("Delete failed");
    }
  };

  const filtered = items.filter((r) => {
    if (statusFilter !== "All" && r.status !== statusFilter) return false;
    const q = search.toLowerCase();
    if (q && !`${r.customer_name} ${r.phone} ${r.service_type} ${r.vehicle_info || ""}`.toLowerCase().includes(q)) return false;
    return true;
  });

  const iconBtn = "p-2 border rounded-sm transition-colors";

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="recurring-tab">
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <Input data-testid="recurring-search" className={`${inputCls} max-w-xs`} placeholder="Search customer, service..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select data-testid="recurring-filter-status" className="h-10 bg-[#0a0a0a] border border-zinc-800 text-white rounded-sm px-3 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="All">Status: All</option>
          {RECURRING_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <p className="text-zinc-600 text-xs ml-auto">Create reminders from a customer record (Customers tab → Manage)</p>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="recurring-empty-state" className="text-center py-24 text-zinc-500">
          <Inbox className="h-12 w-12 mx-auto mb-4 text-zinc-700" />
          No recurring service reminders yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="recurring-list">
          {filtered.map((r) => (
            <div key={r.recurring_id} data-testid={`recurring-row-${r.recurring_id}`} className="bg-[#121212] border border-zinc-800 rounded-xl p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
              <div className="lg:col-span-3">
                <div className="font-heading font-semibold text-white">{r.customer_name}</div>
                <a href={`tel:${(r.phone || "").replace(/[^+\d]/g, "")}`} className="inline-flex items-center gap-1.5 text-blue-400 text-xs"><Phone className="h-3 w-3" /> {r.phone}</a>
                {r.vehicle_info && <div className="text-zinc-500 text-xs">{r.vehicle_info}</div>}
              </div>
              <div className="lg:col-span-3 text-xs">
                <div className="text-white text-sm">{r.service_type}</div>
                {r.last_service_date && <div className="text-zinc-500">Last: {fmtDateOnly(r.last_service_date)}</div>}
                <div className="text-amber-400">Next due: {fmtDateOnly(r.next_service_date)}</div>
              </div>
              <div className="lg:col-span-2 text-xs text-zinc-500">
                {r.last_sent_at ? <>Last reminder: {fmtDate(r.last_sent_at)}</> : "No reminder sent yet"}
                {r.notes && <div className="text-zinc-600 truncate">{r.notes}</div>}
              </div>
              <div className="lg:col-span-4 flex items-center gap-2 flex-wrap lg:justify-end">
                <Badge variant="outline" className={`${recurringStatusColors[r.status]} rounded-sm`}>{r.status}</Badge>
                <button data-testid={`recurring-send-${r.recurring_id}`} onClick={() => setSmsItem(r)} className={`${iconBtn} border-blue-500/50 text-blue-400 hover:bg-blue-500/10`} title="Send reminder text"><MessageSquare className="h-4 w-4" /></button>
                <button data-testid={`recurring-booked-${r.recurring_id}`} onClick={() => patch(r.recurring_id, { status: "Booked" }, "Marked booked")} className={`${iconBtn} border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10`} title="Customer booked"><CheckCircle2 className="h-4 w-4" /></button>
                <button data-testid={`recurring-snooze-${r.recurring_id}`} onClick={() => snooze(r)} className={`${iconBtn} border-purple-500/50 text-purple-400 hover:bg-purple-500/10`} title="Snooze"><Moon className="h-4 w-4" /></button>
                <button data-testid={`recurring-next-${r.recurring_id}`} onClick={() => completeAndNext(r)} className={`${iconBtn} border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10`} title="Complete & create next"><RotateCw className="h-4 w-4" /></button>
                <button data-testid={`recurring-declined-${r.recurring_id}`} onClick={() => patch(r.recurring_id, { status: "Declined" }, "Marked declined")} className={`${iconBtn} border-red-500/50 text-red-400 hover:bg-red-500/10`} title="Declined"><XCircle className="h-4 w-4" /></button>
                <button data-testid={`recurring-delete-${r.recurring_id}`} onClick={() => removeRecurring(r)} className={`${iconBtn} border-zinc-800 text-zinc-500 hover:text-red-400`} title="Delete reminder"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {smsItem && (
        <SmsDialog
          key={`rec-sms-${smsItem.recurring_id}`}
          open
          onOpenChange={(v) => !v && setSmsItem(null)}
          phone={smsItem.phone}
          title="Send Recurring Service Reminder"
          initialMessage={buildRecurringSms(firstName(smsItem.customer_name), smsItem.service_type, config.booking_url)}
          onLogged={logSend(smsItem)}
        />
      )}
    </div>
  );
};
